import { useRef } from "react";
import { motion } from "framer-motion";
import { Dumbbell, Salad, Activity, Scale } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { API, api, mediaUrl, focalStyle } from "../../lib/api";
import { SectionBody } from "../RichText";
import { Link } from "react-router-dom";

const reveal = {
    hidden: { opacity: 0, y: 32 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export const Reveal = ({ children, className = "", delay = 0 }) => (
    <motion.div
        className={className}
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        transition={{ delay }}
    >
        {children}
    </motion.div>
);

export function SectionHeader({ eyebrow, title, subtitle }) {
    return (
        <Reveal className="max-w-3xl mb-14">
            {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance text-ink">{title}</h2>
            {subtitle && <p className="mt-4 text-base sm:text-lg leading-relaxed text-ink-soft">{subtitle}</p>}
        </Reveal>
    );
}

/** Image de section : cadrage « Contenir » (intégrale, ratio naturel) ou « Couvrir »
 * (recadrage autorisé, point focal). Aucun étirement, aucune URL externe. */
export function SectionImage({ sec, alt, coverClass = "h-44", containBg = "bg-ivory", frame = false, testid }) {
    const img = sec?.image;
    if (!img?.url) return null;
    const contain = sec?.image_fit === "contain";
    if (contain) {
        return (
            <div
                className={`relative w-full overflow-hidden ${containBg} ${frame ? "rounded-3xl gold-frame" : ""}`}
                style={img.width && img.height ? { aspectRatio: `${img.width} / ${img.height}` } : undefined}
            >
                <img src={mediaUrl(img.url)} alt={img.alt || alt || ""} loading="lazy" className="w-full h-full object-contain" data-testid={testid} />
            </div>
        );
    }
    return (
        <div className={`relative w-full overflow-hidden ${coverClass} ${frame ? "rounded-3xl gold-frame" : ""}`}>
            <img src={mediaUrl(img.url)} alt={img.alt || alt || ""} loading="lazy" style={focalStyle(img)} className="w-full h-full object-cover" data-testid={testid} />
        </div>
    );
}

export function Marquee() {
    const { data } = useSite();
    const sec = data?.sections?.marquee;
    const words = (sec?.body || "").split(",").map((w) => w.trim()).filter(Boolean);
    if (!sec || sec.visible === false || words.length === 0) return null;
    const doubled = [...words, ...words];
    return (
        <div className="bg-pearl py-6 overflow-hidden border-y border-border" aria-hidden="true">
            <div className="flex whitespace-nowrap animate-marquee w-max">
                {doubled.map((w, i) => (
                    <span key={i} className="mx-8 font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-forest/85 flex items-center gap-8">
                        {w} <span className="text-gold text-xl">✦</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

export function VideoSection() {
    const { data } = useSite();
    const sec = data?.sections?.video;
    const video = sec?.media_display === "none" ? null : sec?.video_media;
    const tracked = useRef(false);
    if (!sec || sec.visible === false) return null;

    const onPlay = () => {
        if (!tracked.current && video) {
            tracked.current = true;
            api.post("/public/track/video-play", { media_id: video.media_id }).catch(() => {});
        }
    };

    return (
        <section className="bg-ivory py-24 sm:py-28" data-testid="video-section">
            <div className="container-bc">
                <SectionHeader eyebrow={sec.eyebrow || "En images"} title={sec.title} subtitle={sec.subtitle} />
                <Reveal>
                    {video ? (
                        <div className="relative max-w-4xl mx-auto">
                            <div className="absolute -inset-2 rounded-3xl border border-gold/50 translate-x-3 translate-y-3" aria-hidden="true" />
                            <video
                                controls
                                preload="metadata"
                                playsInline
                                poster={video.has_thumbnail ? `${API}/media/thumb/${video.media_id}` : undefined}
                                className="relative w-full rounded-3xl gold-frame bg-forest-deep aspect-video"
                                src={`${API}/media/file/${video.media_id}`}
                                onPlay={onPlay}
                                data-testid="public-video-player"
                            >
                                Votre navigateur ne prend pas en charge la lecture vidéo.
                            </video>
                            {(video.title || video.description) && (
                                <div className="mt-6 text-center">
                                    <h3 className="font-heading font-bold text-ink text-xl">{video.title}</h3>
                                    {video.description && <p className="text-ink-soft mt-1">{video.description}</p>}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="max-w-4xl mx-auto rounded-3xl border-2 border-dashed border-gold/50 bg-pearl aspect-video flex flex-col items-center justify-center text-center p-8" data-testid="video-empty-state">
                            <p className="font-heading font-bold text-ink text-xl">La vidéo de présentation arrive bientôt</p>
                            <p className="text-ink-soft mt-2 max-w-md text-sm">
                                Notre équipe prépare la vidéo officielle BODYCOACHIA. Elle sera publiée ici dès qu'elle sera prête.
                            </p>
                        </div>
                    )}
                </Reveal>
            </div>
        </section>
    );
}

const BENTO_META = [
    { key: "bento_sport", icon: Dumbbell, span: "md:col-span-2" },
    { key: "bento_nutrition", icon: Salad, span: "" },
    { key: "bento_suivi", icon: Activity, span: "" },
    { key: "bento_balance", icon: Scale, span: "md:col-span-2" },
];

export function ValueBento() {
    const { data } = useSite();
    const sec = data?.sections?.value;
    if (!sec || sec.visible === false) return null;
    return (
        <section className="bg-pearl py-24 sm:py-28" data-testid="value-section">
            <div className="container-bc">
                <SectionHeader eyebrow={sec.eyebrow || "Pourquoi BODYCOACHIA"} title={sec.title} subtitle={sec.subtitle} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
                    {BENTO_META.map((meta, i) => {
                        const s = data?.sections?.[meta.key];
                        const title = s?.title || "";
                        const hasImg = !!s?.image?.url && s?.media_display !== "none";
                        const contain = s?.image_fit === "contain";
                        return (
                            <Reveal key={meta.key} delay={i * 0.08} className={meta.span}>
                                <article className="group card-light overflow-hidden h-full flex flex-col" data-testid={`value-card-${i}`}>
                                    {hasImg ? (
                                        <SectionImage sec={s} alt={title} coverClass="h-44" testid={`value-card-image-${i}`} />
                                    ) : (
                                        <div className="relative h-44 bg-gradient-to-br from-pearl to-ivory flex items-center justify-center" data-testid={`value-card-image-${i}`}>
                                            <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white shadow border border-gold/30">
                                                <meta.icon className="text-forest" size={22} />
                                            </span>
                                        </div>
                                    )}
                                    <div className="p-6 flex-1">
                                        <h3 className="font-heading font-bold text-xl text-ink flex items-center gap-2.5">
                                            {hasImg && contain && <meta.icon className="text-gold-dark shrink-0" size={19} />}
                                            {title}
                                        </h3>
                                        <SectionBody sec={s} className="text-ink-soft mt-2 text-sm leading-relaxed" testid={`value-card-body-${i}`} />
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export function Steps() {
    const { data } = useSite();
    const sec = data?.sections?.steps;
    if (!sec || sec.visible === false) return null;
    const steps = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => data?.sections?.[`step_${n}`]).filter(Boolean);
    return (
        <section className="bg-ivory py-24 sm:py-28 relative" data-testid="steps-section">
            <div className="container-bc relative z-10">
                <SectionHeader eyebrow={sec.eyebrow || "La méthode"} title={sec.title} subtitle={sec.subtitle} />
                <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {steps.map((s, i) => (
                        <Reveal key={i} delay={i * 0.06}>
                            <li className="h-full card-light p-6 hover:border-gold/60 transition-colors" data-testid={`step-card-${i + 1}`}>
                                <span className="font-mono text-gold-dark text-sm font-semibold">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="font-heading font-bold text-ink text-lg mt-3">{s.title}</h3>
                                <SectionBody sec={s} className="text-ink-soft text-sm mt-2 leading-relaxed" testid={`step-body-${i + 1}`} />
                            </li>
                        </Reveal>
                    ))}
                </ol>
                {sec.cta_label && (
                    <Reveal className="mt-12 text-center">
                        <Link to={sec.cta_href || "/fonctionnement"} className="text-forest font-semibold hover:text-gold-dark underline underline-offset-4 transition-colors" data-testid="steps-cta">
                            {sec.cta_label} →
                        </Link>
                    </Reveal>
                )}
            </div>
        </section>
    );
}

const PILLAR_META = [
    { key: "pillar_sport", icon: Dumbbell },
    { key: "pillar_nutrition", icon: Salad },
    { key: "pillar_suivi", icon: Activity },
    { key: "pillar_balance", icon: Scale },
];

export function Pillars() {
    const { data } = useSite();
    const sec = data?.sections?.pillars;
    if (!sec || sec.visible === false) return null;
    return (
        <section className="bg-pearl py-24 sm:py-28" data-testid="pillars-section">
            <div className="container-bc">
                <SectionHeader eyebrow={sec.eyebrow || "Les 4 piliers"} title={sec.title} subtitle={sec.subtitle} />
                <div className="space-y-20">
                    {PILLAR_META.map((meta, i) => {
                        const p = data?.sections?.[meta.key];
                        if (!p || p.visible === false) return null;
                        const reverse = i % 2 === 1;
                        const hasImg = !!p.image?.url && p.media_display !== "none";
                        const contain = p.image_fit === "contain";
                        return (
                            <div key={meta.key} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center" data-testid={`pillar-${meta.key}`}>
                                <Reveal className={`md:col-span-6 ${reverse ? "md:order-2" : ""}`}>
                                    <div className="relative">
                                        <div className={`absolute -inset-2 rounded-3xl border border-gold/50 ${reverse ? "-translate-x-3" : "translate-x-3"} translate-y-3`} aria-hidden="true" />
                                        {hasImg ? (
                                            <SectionImage
                                                sec={p}
                                                alt={p.title}
                                                coverClass="aspect-[4/3]"
                                                frame
                                                testid={`pillar-image-${meta.key}`}
                                            />
                                        ) : (
                                            <div className="relative rounded-3xl gold-frame bg-gradient-to-br from-pearl to-ivory aspect-[4/3] flex items-center justify-center" data-testid={`pillar-image-${meta.key}`}>
                                                <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow border border-gold/30">
                                                    <meta.icon className="text-forest" size={30} />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </Reveal>
                                <Reveal className={`md:col-span-6 ${reverse ? "md:order-1" : ""}`} delay={0.1}>
                                    <p className="eyebrow mb-3">{p.subtitle}</p>
                                    <h3 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight">{p.title}</h3>
                                    <SectionBody sec={p} className="text-ink-soft mt-4 leading-relaxed" testid={`pillar-body-${meta.key}`} />
                                </Reveal>
                            </div>
                        );
                    })}
                </div>
                {sec.cta_label && (
                    <Reveal className="mt-16 text-center">
                        <Link to={sec.cta_href || "/fonctionnalites"} className="inline-block bg-forest text-ivory px-8 py-3 rounded-full font-semibold hover:bg-forest-surface transition-colors" data-testid="pillars-cta">
                            {sec.cta_label}
                        </Link>
                    </Reveal>
                )}
            </div>
        </section>
    );
}
